import type { ExternalHealth } from '@console/types'
import styles from './DegradationBanner.module.css'

export interface DegradationBannerProps {
  health: ExternalHealth
}

const SYSTEM_LABEL = {
  zoho: 'Zoho',
  llmProvider: 'Ask',
  cadence: 'Cadence',
} as const

const MEANING: Record<keyof typeof SYSTEM_LABEL, string> = {
  zoho: 'New runs may be delayed; existing runs and receipts render normally.',
  llmProvider: 'Runs page and filters work normally.',
  cadence: 'Live data cannot be fetched right now.',
}

/**
 * Addendum §A: state about external systems, orthogonal to the surface's
 * own loading/empty/error state. Renders nothing when everything is `ok` —
 * this banner appears only when there is something to say.
 */
export function DegradationBanner({ health }: DegradationBannerProps) {
  const degraded = (Object.keys(SYSTEM_LABEL) as (keyof typeof SYSTEM_LABEL)[]).filter(
    (key) => health[key].status !== 'ok',
  )

  if (degraded.length === 0) return null

  return (
    <div className={styles['banner']} role="status">
      {degraded.map((key) => {
        const state = health[key]
        const stateWord =
          state.status === 'unreachable' ? 'currently unreachable' : 'currently degraded'
        return (
          <p key={key} className={styles['item']}>
            {SYSTEM_LABEL[key]} is {stateWord}. {state.detail ?? MEANING[key]}
          </p>
        )
      })}
    </div>
  )
}
